import React, { useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { UploadCloud, Loader2, X, Link as LinkIcon, Sparkles, Wand2 } from "lucide-react";
import { endpoints } from "@/api";
import { toast } from "sonner";
import { useI18n } from "@/i18n";
import { useAdmin } from "@/hooks/useAdmin";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

// Build a full URL for image src. Asset URLs are stored as "/api/assets/<id>".
export const resolveAssetUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://") || url.startsWith("data:")) return url;
  if (url.startsWith("/api/assets/")) return `${BACKEND}${url}`;
  return url;
};

/**
 * Image uploader with drag-and-drop + click + URL paste.
 * Stores asset on backend and returns "/api/assets/<id>" path via onChange.
 */
export default function ImageUploader({ value, onChange, label, testid = "image-uploader", aspect = "video", aiPromptSuggestion = "" }) {
  const { isRTL } = useI18n();
  const isAdmin = useAdmin();
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [showAi, setShowAi] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const preview = resolveAssetUrl(value);

  const upload = async (file) => {
    if (!file) return;
    if (!file.type?.startsWith("image/")) {
      toast.error(isRTL ? "الملف ليس صورة" : "Not an image file");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error(isRTL ? "الحجم أكبر من 5 MB" : "File larger than 5 MB");
      return;
    }
    setBusy(true);
    try {
      const data = await endpoints.adminUploadImage(file);
      onChange(data.url);
      toast.success(isRTL ? "تم رفع الصورة" : "Image uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isRTL ? "تعذّر الرفع" : "Upload failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={testid}>
      {label && <label className="text-xs uppercase tracking-widest text-[#5C544F]">{label}</label>}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          const f = e.dataTransfer.files?.[0];
          if (f) upload(f);
        }}
        onClick={() => !busy && inputRef.current?.click()}
        className={`mt-1 relative rounded-xl border-2 border-dashed cursor-pointer transition-all overflow-hidden
          ${dragOver ? "border-[#C86240] bg-[#C86240]/10" : "border-[#2A2421]/15 bg-[#F9F6F0] hover:bg-white"}
          ${aspect === "video" ? "aspect-video" : "aspect-square"}`}
      >
        {preview ? (
          <>
            <img src={preview} alt="" className="absolute inset-0 w-full h-full object-cover" />
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onChange(""); }}
              className="absolute top-2 end-2 bg-black/60 hover:bg-black/80 text-white rounded-full p-1"
              data-testid={`${testid}-clear`}
              aria-label="Remove"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </>
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-[#5C544F] gap-2 text-center px-4">
            {busy ? <Loader2 className="w-6 h-6 animate-spin" /> : <UploadCloud className="w-7 h-7" />}
            <div className="text-xs">
              {busy
                ? (isRTL ? "جاري الرفع..." : "Uploading...")
                : (isRTL ? "اسحب صورة هنا أو اضغط للاختيار" : "Drag an image here or click to choose")}
            </div>
            <div className="text-[10px] opacity-70">JPG / PNG / WEBP · max 5 MB</div>
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => upload(e.target.files?.[0])}
          data-testid={`${testid}-file-input`}
        />
      </div>

      <div className="mt-2 flex items-center gap-2">
        <LinkIcon className="w-3 h-3 text-[#5C544F]" />
        <Input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={isRTL ? "أو ألصق رابط صورة..." : "Or paste an image URL..."}
          className="h-8 text-xs"
          data-testid={`${testid}-url-input`}
        />
        {value && (
          <Button type="button" variant="ghost" size="sm" className="h-8 px-2" onClick={() => onChange("")} data-testid={`${testid}-reset`}>
            {isRTL ? "مسح" : "Clear"}
          </Button>
        )}
      </div>

      <div className="mt-2">
        {isAdmin && (
        <button
          type="button"
          onClick={() => { setShowAi((s) => !s); if (!aiPrompt && aiPromptSuggestion) setAiPrompt(aiPromptSuggestion); }}
          className="text-xs text-[#C86240] inline-flex items-center gap-1 hover:underline"
          data-testid={`${testid}-ai-toggle`}
        >
          <Wand2 className="w-3 h-3" />
          {isRTL ? "أو ولّد صورة بالذكاء الاصطناعي" : "Or generate with AI"}
        </button>
        )}
        {isAdmin && showAi && (
          <div className="mt-2 p-3 rounded-xl bg-gradient-to-br from-[#C86240]/8 via-white to-[#F9F6F0] border border-[#C86240]/15 space-y-2">
            <Input
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              placeholder={isRTL ? "صف الصورة... مثال: قهوة V60 مع بخار وإضاءة دافئة" : "Describe the image... e.g. V60 pour with steam, warm light"}
              className="h-8 text-xs"
              data-testid={`${testid}-ai-prompt`}
            />
            <Button
              type="button"
              disabled={aiBusy || aiPrompt.trim().length < 5}
              onClick={async () => {
                setAiBusy(true);
                try {
                  const data = await endpoints.adminGenerateImage(aiPrompt.trim());
                  onChange(data.url);
                  toast.success(isRTL ? "تم توليد الصورة" : "Image generated");
                  setShowAi(false);
                } catch (e) {
                  toast.error(e?.response?.data?.detail || (isRTL ? "تعذّر التوليد" : "Generation failed"));
                } finally {
                  setAiBusy(false);
                }
              }}
              className="h-8 px-3 text-xs bg-gradient-to-r from-[#C86240] to-[#a04d31] hover:from-[#b15637] hover:to-[#8e4329] text-white rounded-full w-full"
              data-testid={`${testid}-ai-generate`}
            >
              {aiBusy ? <Loader2 className="w-3 h-3 me-1 animate-spin" /> : <Sparkles className="w-3 h-3 me-1" />}
              {aiBusy ? (isRTL ? "جاري التوليد..." : "Generating...") : (isRTL ? "توليد الصورة" : "Generate image")}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
